import React, { useEffect, useState } from 'react'
import { useAppSelector } from '@state/hooks'
import { omapApi } from '@services/omapApi'
import { meceToPriorities } from './strategyMapHelpers'
import './CanvasHeaderStrip.css'

interface CompanyProfileBundle {
  vision?: string
  mission?: string
}

export const CanvasHeaderStrip: React.FC = () => {
  const activeCompanyId = useAppSelector(s => s.companies.activeCompanyId)
  const companyId = activeCompanyId
    ? parseInt(activeCompanyId.replace('company_', ''), 10)
    : null
  const [bundle, setBundle] = useState<CompanyProfileBundle>({})

  const meceTable = useAppSelector(s => {
    const activeId = s.conversations.activeConversationId
    if (!activeId) return null
    const conv = s.conversations.conversations.find(c => c.id === activeId)
    return conv?.strategyWorkspaceData?.meceTable ?? null
  })

  useEffect(() => {
    if (!companyId) return
    let cancelled = false
    omapApi
      .getAllCompanyProfileValues(companyId)
      .then(values => {
        if (cancelled) return
        setBundle({
          vision: values['competitive_vision_id'],
          mission: values['company_mission_id'],
        })
      })
      .catch(() => { /* empty state stays */ })
    return () => { cancelled = true }
  }, [companyId])

  const priorities = meceToPriorities(meceTable, 3)

  return (
    <header className="canvas-kn-header-strip" aria-label="Strategy header">
      <div className="canvas-kn-header-table">
        <div className="canvas-kn-header-row canvas-kn-header-row--full">
          <div className="canvas-kn-header-label-cell">Vision</div>
          <div className="canvas-kn-header-body-cell canvas-kn-header-body-cell--full">
            {bundle.vision ? (
              <p className="canvas-kn-header-cell-body">{bundle.vision}</p>
            ) : (
              <p className="canvas-kn-header-cell-empty">
                Statement of organization's vision
              </p>
            )}
          </div>
        </div>
        <div className="canvas-kn-header-row canvas-kn-header-row--full">
          <div className="canvas-kn-header-label-cell">Mission</div>
          <div className="canvas-kn-header-body-cell canvas-kn-header-body-cell--full">
            {bundle.mission ? (
              <p className="canvas-kn-header-cell-body">{bundle.mission}</p>
            ) : (
              <p className="canvas-kn-header-cell-empty">
                Statement of organization's mission
              </p>
            )}
          </div>
        </div>
        <div className="canvas-kn-header-row canvas-kn-header-row--priorities">
          <div className="canvas-kn-header-label-cell">Strategic Priority</div>
          {[0, 1, 2].map(i => {
            const p = priorities[i]
            return (
              <div key={i} className="canvas-kn-header-body-cell">
                <div className="canvas-kn-header-priority-name">
                  {p?.name || `Priority #${i + 1}`}
                </div>
                {p?.description ? (
                  <p className="canvas-kn-header-cell-body">{p.description}</p>
                ) : (
                  <p className="canvas-kn-header-cell-empty">
                    Strategic priority #{i + 1} not yet defined.
                  </p>
                )}
              </div>
            )
          })}
        </div>
        <div className="canvas-kn-header-row canvas-kn-header-row--priorities">
          <div className="canvas-kn-header-label-cell">Strategic Result</div>
          {[0, 1, 2].map(i => {
            const result = priorities[i]?.strategic_result
            return (
              <div key={i} className="canvas-kn-header-body-cell">
                {result ? (
                  <p className="canvas-kn-header-cell-body">{result}</p>
                ) : (
                  <p className="canvas-kn-header-cell-empty">
                    Statement of outcome for priority #{i + 1}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </header>
  )
}
